<template>
  <div id="content-editor" v-if="dataLoaded">
    <!-- Inline -->
    <slot v-bind:data="state" name="state" />
    <div class="row sticky q-mb-md">
      <q-page-sticky expand position="top" style="z-index: 10;">
        <q-toolbar
          text-color="grey-7"
          class="q-pa-md q-pr-lg"
          :class="{ 'sticky-shadow': testScroll }"
          style="background: #e7e7e7 !important"
        >
          <q-btn
            class="capitalize ctrl-button q-ml-md"
            color="grey-5"
            :label="`${$store.state.stringsStore.strings.action.toOverview}`"
            @click="$router.push(`/${module}`)"
            icon="keyboard_backspace"
          />
          <q-toolbar-title></q-toolbar-title>
          <!-- <q-btn v-if="!$route.params.id"
              class="capitalize ctrl-button right q-mr-md"
              :color="'primary'"
              label="Load template"
              @click="fetchTemplates"
            /> -->
          <q-btn
            class="capitalize ctrl-button bg-ctrl-grey right q-mr-md"
            color="ctrl-turquoise"
            label="Template"
            :disabled="disable"
            :class="{ 'button-active': true }"
          >
            <q-icon size="large" name="mdi-chevron-down" />
            <!-- Direct child of target -->
            <q-popover anchor="bottom middle" self="top middle">
              <q-list link>
                <q-item
                  v-if="$route.params.id"
                  class="toolbar-list-item"
                  @click.native="openTemplateModal"
                >
                  {{
                    $store.state.stringsStore.language === 'nl'
                      ? 'Opslaan als template'
                      : 'Save as template'
                  }}
                </q-item>
                <q-item class="toolbar-list-item" @click.native="fetchTemplates">
                  {{
                    $store.state.stringsStore.language === 'nl'
                      ? 'Template gebruiken'
                      : 'Load a template'
                  }}
                </q-item>
              </q-list>
            </q-popover>
          </q-btn>
          <q-btn
            v-if="data.preview_hash"
            class="capitalize ctrl-button right q-mr-md"
            :color="'primary'"
            :label="getString('action.preview')"
            icon="remove_red_eye"
            @click="preview"
          />
          <q-btn
            v-if="
              canDelete &&
                ($store.state.meStore.me.rights.includes(`${permission}.delete`) ||
                  $store.state.meStore.me.su) &&
                $route.params.id
            "
            class="q-mr-md capitalize ctrl-button right"
            icon-right="delete"
            color="negative capitalize"
            :label="getString('action.delete')"
            align="right"
            @click="deletePost()"
          />
          <template v-if="updatePossible">
            <q-btn
              v-if="disable"
              ref="save"
              class="capitalize ctrl-button right q-mr-lg"
              color="grey-5"
              :label="getString('action.edit')"
              @click="checkAndSave(false)"
              icon-right="edit"
            />
            <q-btn
              v-else
              class="capitalize ctrl-button bg-ctrl-grey right q-mr-md"
              color="ctrl-turquoise"
              label="Save"
              :disabled="disable"
              :class="{ 'button-active': true }"
            >
              <q-icon size="large" name="mdi-chevron-down" />
              <!-- Direct child of target -->
              <q-popover anchor="bottom middle" self="top middle">
                <q-list link>
                  <q-item
                    v-if="$route.params.id"
                    class="toolbar-list-item"
                    @click.native="checkAndSave(true)"
                  >
                    {{
                      $store.state.stringsStore.language === 'nl'
                        ? 'Opslaan als update'
                        : 'Save as update'
                    }}
                  </q-item>
                  <q-item class="toolbar-list-item" @click.native="checkAndSave(false)">
                    {{ $store.state.stringsStore.language === 'nl' ? 'Opslaan' : 'Save' }}
                  </q-item>
                </q-list>
              </q-popover>
            </q-btn>
          </template>
          <template v-else>
            <q-btn
              ref="save"
              class="capitalize ctrl-button right q-mr-lg"
              :color="disable ? 'grey-5' : 'ctrl-turquoise'"
              :label="disable ? getString('action.edit') : getString('action.save')"
              @click="checkAndSave(false)"
              :icon-right="disable ? 'edit' : 'save'"
            />
          </template>
        </q-toolbar>
      </q-page-sticky>
    </div>

    <div class="row q-mb-md">
      <!-- Left column -->
      <div class="col-md-7 col-sm-12 col-xs-12 q-pr-sm">
        <div
          id="titlebar"
          class="q-mt-lg card-block row flex-between sticky-editor sticky-editor__top"
        >
          <div class="q-ma-sm wrap-title">
            {{ $store.state.stringsStore.language === 'nl' ? 'Pagina titel: ' : 'Page title: '
            }}<span class="page-title">{{ data.name }}{{ data.title }}</span>
          </div>
          <div>
            <q-btn
              v-if="data.history"
              class="capitalize ctrl-button q-mr-md"
              :color="displayHistory ? 'primary' : 'grey-5'"
              @click="toggleHistory"
              :icon="'history'"
            />
            <q-btn
              class="capitalize ctrl-button"
              color="secondary"
              :label="
                minimized
                  ? getString('editor.button.expand.all')
                  : getString('editor.button.collapse.all')
              "
              @click="expandCollapseAll"
              :icon="minimized ? 'mdi-arrow-expand' : 'mdi-arrow-collapse'"
            />
          </div>
        </div>

        <transition name="fade" mode="out-in">
          <div class="full-width q-mt-md" v-show="displayHistory">
            <div class="card-block scroll">
              <q-timeline responsive color="info">
                <q-timeline-entry
                  v-for="item in data.history"
                  :key="item.id"
                  :title="getTimeLineTitle(item)"
                  :subtitle="formatDateTime(item.datetime)"
                  :color="item.type === 'add' ? 'positive' : 'info'"
                  side="left"
                  :icon="item.type"
                >
                  <!-- Disable until later -->
                  <!-- <q-btn
                  class="capitalize ctrl-button"
                  icon="remove_red_eye"
                  :color="item.type === 'add' ? 'positive' : 'info'"
                  v-show="data.history.length > 1 && i !== data.history.length - 1 && i !== 0"
                  @click="alertTitle(item.id)">
                  </q-btn>-->
                </q-timeline-entry>
              </q-timeline>
            </div>
          </div>
        </transition>

        <div class="sticky-editor sticky-editor__content">
          <!-- intro -->
          <div v-if="fieldNames.includes('intro')" class="q-mt-md q-mb-md">
            <div class="full-width">
              <p class="card-title">Intro</p>
              <div class="card-block scroll">
                <!-- Field: HTML Editor -->
                <tiny-editor
                  tinymce-script-src="https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.11/tinymce.min.js"
                  :init="tinyConfig()"
                  ref="tm"
                  :disabled="disable"
                  v-model="data.intro"
                >
                </tiny-editor>
              </div>
            </div>
          </div>

          <div class="q-mt-lg card-block card-block__dark row flex-between">
            <draggable
              class="full-width"
              v-model="contentBlocks"
              handle=".handle"
              v-bind="dragOptions"
              @start="drag = true"
              @end="drag = false"
            >
              <transition-group type="transition" :name="!drag ? 'flip-list' : null">
                <div
                  :class="drag ? 'q-mb-lg q-mt-lg' : ''"
                  v-for="(block, i) in contentBlocks"
                  :key="block.guid"
                  v-bind:item="block"
                  v-bind:index="i"
                >
                  <content-block
                    :linkedIds="linkedIds"
                    slot="header"
                    :guid="block.guid"
                    :uid="block.id"
                    :error="blockError(block.guid)"
                    :fullHeight="!disable"
                    :disabled="disable"
                    :blockType="block.code"
                    :content="data.content && data.content[i]"
                    @duplicate="duplicateBlock(...arguments, i)"
                  />

                  <div class="add-block" v-show="!drag && i !== contentBlocks.length - 1">
                    <div class="block-seperator"></div>
                    <q-btn
                      :disable="disable"
                      @click="openBlockModal(i)"
                      color="tertiary"
                      size="sm"
                      label="Add content block"
                      icon="add"
                    />
                  </div>
                </div>
              </transition-group>
            </draggable>
            <div class="full-width">
              <div class="add-block" v-show="!drag">
                <div class="block-seperator"></div>
                <q-btn
                  :disable="disable"
                  @click="openBlockModal(i)"
                  :color="contentMissing && !contentBlocks.length ? 'negative' : 'tertiary'"
                  size="sm"
                  label="Add content block"
                  icon="add"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Right column -->
      <div class="col-md-5 col-sm-12 col-xs-12 q-pl-sm">
        <!-- General block -->
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">{{ getString('general') }}</p>
            <div class="card-block scroll">
              <!-- Title -->
              <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('title')"
                type="text"
                :error="fieldErrors.title && !data.title"
                class="no-shadow full-width q-mb-md"
                :float-label="getString('title')"
                @input="suggestUrl"
                v-model="data.title"
              />

              <!-- Name -->
              <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('name')"
                type="text"
                @input="suggestUrl"
                :error="fieldErrors.name && !data.name"
                class="no-shadow full-width q-mb-md"
                :float-label="getString('name')"
                v-model="data.name"
              />

              <!-- Url -->
              <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('url') && data.index !== true"
                :error="fieldErrors.url && !data.url"
                type="text"
                class="full-width q-mb-md"
                @input="cleanUrl"
                :float-label="getString('url')"
                v-model="data.url"
              />

              <!-- Parent -->
              <q-input
                v-if="fieldNames.includes('parentPage')"
                v-model="data.parent.name"
                :disable="disable"
                :readonly="disable"
                clearable
                @clear="setParentPage({ id: '0', label: null })"
                float-label="Parent"
              >
                <q-autocomplete
                  @search="searchParentPage"
                  :min-characters="2"
                  @selected="setParentPage"
                />
              </q-input>

              <!-- Online -->
              <q-toggle
                class="q-mt-md q-mr-md"
                color="secondary"
                v-if="fieldNames.includes('online')"
                v-model="data.online"
                :disable="disable"
                :readonly="disable"
              >
                <span style="margin-left: 1em;">
                  {{ getString('editor.block.field.online') }}
                </span>
              </q-toggle>

              <!-- Categories -->
              <chips-autocomplete
                v-if="fieldNames.includes('categories')"
                class="q-pb-md"
                :disable="disable"
                :error="fieldErrors.categories && data.categories.length === 0"
                :errorLabel="
                  $store.state.stringsStore.language === 'nl'
                    ? 'Dit veld is verplicht'
                    : 'This field is required'
                "
                :selection="data.categories"
                :name="'Categories'"
                :labelTerm="'name'"
                :endpoint="'category'"
                :resultLabel="'categories'"
                :promptNewMessage="getString('module.categories.add')"
              />

              <!-- Branches -->
              <chips-autocomplete
                v-if="fieldNames.includes('branches') && !reference"
                :disable="disable"
                :error="fieldErrors.branches && data.branches.length === 0"
                :errorLabel="
                  $store.state.stringsStore.language === 'nl'
                    ? 'Dit veld is verplicht'
                    : 'This field is required'
                "
                class="q-pb-md"
                :selection="data.branches"
                :name="'Branches'"
                :labelTerm="'name'"
                :endpoint="'branch'"
                :resultLabel="'branches'"
              />

              <!-- Gallery -->
              <q-toggle
                v-if="fieldNames.includes('lightbox')"
                class="q-mt-md  q-mr-md"
                color="secondary"
                v-model="data.lightbox"
                :disable="disable"
                :readonly="disable"
              >
                <span style="margin-left: 1em;">
                  Enable lightbox
                </span>
              </q-toggle>

              <!-- Reading confirmation -->
              <q-toggle
                v-if="fieldNames.includes('read-confirmation')"
                class="q-mt-md"
                color="secondary"
                v-model="data.read_confirmation"
                :disable="disable"
                :readonly="disable"
              >
                <span style="margin-left: 1em;">
                  Require read confirmation
                </span>
              </q-toggle>
            </div>
          </div>
        </div>
        <!-- Image block -->
        <div
          class="row q-mb-sm"
          v-if="fieldNames.includes('image') && !fieldNames.includes('recap')"
        >
          <div class="full-width">
            <p class="card-title">{{ getString('block.field.default.header.image') }}</p>
            <div class="card-block scroll">
              <div class="row">
                <div class="col-md-8 q-mr-md">
                  <div
                    class="card-block image-preview image flex-center"
                    :class="fieldErrors.image && !data.image.location && 'card-block__error'"
                  >
                    <img
                      v-if="imgSrc"
                      class="image-preview__small centered"
                      :src="`${buildFileLocation(imgSrc)}?timestamp=${Date.now()}`"
                      alt=""
                    />
                  </div>
                </div>
                <div class="col">
                  <q-btn
                    class="capitalize ctrl-button"
                    :color="disable ? 'grey-5' : 'primary'"
                    :disabled="disable"
                    :label="getString('editor.button.image.select')"
                    @click="!disable && setModalContext(null, true)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Extended block -->
        <div class="row q-mb-sm" v-if="fieldNames.includes('recap')">
          <div class="full-width">
            <p class="card-title">{{ getString('editor.block.header.recap') }}</p>
            <div class="card-block scroll">
              <div class="row">
                <div class="col-md-6 q-mr-md">
                  <div
                    class="card-block image-preview image flex-center"
                    :class="fieldErrors.image && !data.image.location && 'card-block__error'"
                  >
                    <img
                      v-if="imgSrc"
                      class="image-preview__small centered"
                      :src="`${buildFileLocation(imgSrc)}?timestamp=${Date.now()}`"
                      alt=""
                    />
                  </div>
                </div>
                <div class="col">
                  <q-input
                    :error="fieldErrors.recap && !data.recap"
                    type="textarea"
                    :disable="disable"
                    :readonly="disable"
                    class="no-shadow full-width q-mb-md"
                    :float-label="getString('module.blog.details.general.recap')"
                    v-model="data.recap"
                  />
                  <q-btn
                    class="capitalize ctrl-button"
                    :color="disable ? 'grey-5' : 'primary'"
                    :disable="disable"
                    :label="getString('editor.button.image.select')"
                    @click="disable ? null : (imageModal = true)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Publication block -->
        <div class="row q-mb-sm" v-if="publication">
          <div class="full-width">
            <p class="card-title">{{ getString('editor.block.header.publication') }}</p>
            <div class="card-block scroll">
              <q-input
                v-if="hasAuthor"
                type="text"
                class="full-width q-mb-md"
                :float-label="getString('module.blog.details.publication.author')"
                readonly
                v-model="author"
              />
              <div class="row">
                <div class="col q-mr-sm">
                  <q-select
                    :disable="disable"
                    :readonly="disable"
                    class="full-width q-mb-md"
                    :error="fieldErrors.status && !data.status"
                    placeholder="Publish"
                    :float-label="
                      getString('module.blog.details.publication.date.datepicker.label')
                    "
                    v-model="data.status"
                    :options="publicationOptions"
                    @input="publicationChange()"
                  />
                </div>
                <div class="col q-ml-sm">
                  <!-- Publication date -->
                  <q-datetime
                    format24h
                    :error="fieldErrors.datetime"
                    class="full-width q-mb-md"
                    :disable="this.data.status !== 'published' || disable"
                    :readonly="this.data.status !== 'published'"
                    :float-label="
                      getString('module.blog.details.publication.date.datepicker.label')
                    "
                    v-model="data.datetime_online"
                    type="datetime"
                  />
                </div>
              </div>
              <div class="row">
                <q-toggle
                  class="q-mt-md"
                  color="secondary"
                  v-if="fieldNames.includes('internal')"
                  v-model="data.internal"
                  :disable="disable"
                  :readonly="disable"
                  @input="handleToggle('internal', data.internal)"
                >
                  <span style="margin-left: 1em;">
                    {{ getString('pubilcation.internal') }}
                  </span>
                </q-toggle>
              </div>

              <!-- Target Groups for logged contact people -->
              <div v-if="fieldNames.includes('auth_contact_people')">
                <div class="row q-mb-sm">
                  <q-toggle
                    class="q-mt-md"
                    color="secondary"
                    v-model="data.auth_contact_people"
                    :disable="disable"
                    :readonly="disable"
                    @input="handleToggle('auth_contact_people', data.auth_contact_people)"
                  >
                    <span style="margin-left: 1em;">
                      {{ getString('publication.auth.contact.people.label') }}</span
                    >
                  </q-toggle>
                </div>
                <div class="row q-mb-sm" v-if="data.auth_contact_people">
                  <div class="full-width">
                    <q-select
                      multiple
                      :disable="disable"
                      :readonly="disable"
                      class="full-width q-mb-md"
                      :error="
                        fieldErrors.auth_contact_people_groups && !data.auth_contact_people_groups
                      "
                      :float-label="
                        getString('publication.auth.contact.people.target.groups.label')
                      "
                      v-model="data.auth_contact_people_groups"
                      :options="targetGroupsOptions"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Resource Author block -->
        <div class="row q-mb-sm" v-if="fieldNames.includes('resource_author')">
          <div class="full-width">
            <p class="card-title">{{ getString('editor.block.header.resource_author') }}</p>
            <div class="card-block scroll">
              <!-- Resources for author -->
              <q-input
                v-model="data.resource_author.name"
                :disable="disable"
                :readonly="disable"
                clearable
                @clear="data.resource_author = { id: null, name: null }"
                :float-label="getString('editor.block.placeholder.resource_author')"
              >
                <q-autocomplete
                  @search="searchResources"
                  :min-characters="2"
                  @selected="setResourceAuthor"
                />
              </q-input>
            </div>
          </div>
        </div>
        <!-- Targets block -->
        <div class="row q-mb-sm" v-if="fieldNames.includes('notes')">
          <div class="full-width">
            <p class="card-title">
              {{ $store.state.stringsStore.language === 'nl' ? 'Notities' : 'Notes' }}
            </p>
            <div class="card-block scroll">
              <!-- Notes -->
              <q-input
                type="textarea"
                class="full-width q-mb-md"
                :disable="disable"
                :placeholder="
                  $store.state.stringsStore.language === 'nl'
                    ? 'Schrijf hier je notities'
                    : 'Write your notes here'
                "
                v-model="data.notes"
              />
            </div>
          </div>
        </div>
        <!-- Tags block -->
        <div class="row q-mb-sm" v-if="fieldNames.includes('tags')">
          <div class="full-width">
            <p class="card-title">{{ getString('editor.block.header.tags') }}</p>
            <div class="card-block scroll">
              <!-- Tags -->
              <chips-autocomplete
                class="q-pb-md"
                :disable="disable"
                :selection="data.tags"
                :name="'Tags'"
                :labelTerm="'label'"
                :endpoint="'tag'"
                :resultLabel="'tags'"
                :promptNewMessage="getString('module.tags.add')"
              />
            </div>
          </div>
        </div>
        <!-- Targets block -->
        <div class="row q-mb-sm" v-if="fieldNames.includes('targets')">
          <div class="full-width">
            <p class="card-title">{{ getString('editor.block.header.targets') }}</p>
            <div class="card-block scroll">
              <!-- Targets -->
              <q-select
                multiple
                :disable="disable"
                :readonly="disable"
                :error="fieldErrors.targets && data.targets.length === 0"
                v-model="data.targets"
                :options="targetOptions"
                :float-label="getString('editor.block.header.targets')"
              />
            </div>
          </div>
        </div>
        <!-- Reference specific fields (required) -->
        <div class="row q-mb-sm" v-if="reference">
          <div class="full-width">
            <p class="card-title">{{ getString('module.reference.detail.reference') }}</p>
            <div class="card-block scroll">
              <div class="row">
                <div class="col-9 q-pr-md">
                  <q-input
                    :disable="disable"
                    :readonly="disable"
                    v-if="fieldNames.includes('city')"
                    type="text"
                    :error="fieldErrors.city && !data.address.city"
                    class="no-shadow full-width q-mb-md"
                    :float-label="getString('module.reference.detail.city')"
                    v-model="data.address.city"
                  />
                </div>
                <div class="col-3">
                  <q-input
                    :disable="disable"
                    :readonly="disable"
                    v-if="fieldNames.includes('zipcode')"
                    type="text"
                    :error="fieldErrors.zipcode && !data.address.zipcode"
                    class="no-shadow full-width q-mb-md"
                    :float-label="getString('module.reference.detail.zipcode')"
                    v-model="data.address.zipcode"
                  />
                </div>
              </div>

              <div class="row">
                <div class="col-9 q-pr-md">
                  <q-input
                    :disable="disable"
                    :readonly="disable"
                    v-if="fieldNames.includes('street')"
                    type="text"
                    :error="fieldErrors.street && !data.address.street"
                    class="no-shadow full-width q-mb-md"
                    :float-label="getString('module.reference.detail.street')"
                    v-model="data.address.street"
                  />
                </div>
                <div class="col-3">
                  <q-input
                    :disable="disable"
                    :readonly="disable"
                    v-if="fieldNames.includes('streetnumber')"
                    type="text"
                    :error="fieldErrors.streetnumber && !data.address.streetnumber"
                    class="no-shadow full-width q-mb-md"
                    :float-label="getString('module.reference.detail.streetnumber')"
                    v-model="data.address.streetnumber"
                  />
                </div>
              </div>
              <q-select
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('implementation')"
                :options="dateRangeYear(4, 4)"
                :error="fieldErrors.implementation && !data.implementation"
                v-model="data.implementation"
                class="no-shadow full-width q-mb-md"
                :float-label="getString('module.reference.detail.implementation')"
              />
              <!-- <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('implementation')"
                type="number"
                :error="fieldErrors.implementation && !data.implementation"
                class="no-shadow full-width q-mb-md"
                :float-label="getString('implementation')"
                v-model="data.implementation"
              /> -->
              <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('client')"
                :error="fieldErrors.client && !data.client"
                type="text"
                class="full-width q-mb-md"
                :float-label="getString('module.reference.detail.client')"
                v-model="data.client"
              />
              <chips-autocomplete
                v-if="fieldNames.includes('branches')"
                :disable="disable"
                :error="fieldErrors.branches && data.branches.length === 0"
                :errorLabel="
                  $store.state.stringsStore.language === 'nl'
                    ? 'Dit veld is verplicht'
                    : 'This field is required'
                "
                class="q-pb-md"
                :selection="data.branches"
                :name="'Branches'"
                :labelTerm="'name'"
                :endpoint="'branch'"
                :resultLabel="'branches'"
              />
              <div class="row">
                <q-toggle
                  class="q-mt-md"
                  color="secondary"
                  v-if="fieldNames.includes('map')"
                  v-model="data.map"
                  :disable="disable"
                  :readonly="disable"
                >
                  <span style="margin-left: 1em;">
                    {{ 'Show on map' }}
                  </span>
                </q-toggle>
              </div>
            </div>
          </div>
        </div>
        <div class="row q-mb-sm" v-if="fieldNames.includes('cover')">
          <div class="full-width">
            <p class="card-title">{{ getString('editor.block.header.cover') }}</p>
            <div class="card-block scroll">
              <div class="row">
                <div class="col">
                  <q-input
                    clearable
                    @clear="removeCover()"
                    type="text"
                    :error="fieldErrors.cover && !data.cover"
                    :disable="disable"
                    :readonly="disable"
                    class="full-width q-mb-md"
                    :float-label="getString('file')"
                    v-model="data.cover.name"
                    @click="!disable ? (fileModal = true) : null"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Reference specific fields (optional) -->
        <div class="row q-mb-sm" v-if="reference">
          <div class="full-width">
            <p class="card-title">Project</p>
            <div class="card-block scroll">
              <!-- q-autocomplete for project number -->
              <q-search
                clearable
                v-model="data.project.number"
                class="full-width q-mb-md"
                :disable="disable"
                @clear="data.project = { id: null, number: null }"
                float-label="Project"
              >
                <q-autocomplete
                  :max-results="25"
                  value-field="id"
                  :min-characters="2"
                  @search="searchProjects"
                  @selected="selectProject"
                />
              </q-search>
              <q-datetime
                format24h
                class="full-width q-mb-md"
                v-if="fieldNames.includes('delivery_date')"
                :disable="disable"
                :readonly="disable"
                :error="fieldErrors.delivery_date && !data.delivery_date"
                :float-label="getString('module.reference.detail.deliveryDate')"
                v-model="data.delivery_date"
                type="date"
              />
              <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('architect')"
                type="text"
                :error="fieldErrors.architect && !data.architect"
                class="no-shadow full-width q-mb-md"
                :float-label="getString('module.reference.detail.architect')"
                v-model="data.architect"
              />
              <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('architectural_design')"
                :error="fieldErrors.architectural_design && !data.architectural_design"
                type="text"
                class="full-width q-mb-md"
                :float-label="getString('module.reference.detail.architecturalDesign')"
                v-model="data.architectural_design"
              />
              <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('project_scope')"
                :suffix="'&#13217;'"
                :error="fieldErrors.project_scope && !data.project_scope"
                type="number"
                class="full-width q-mb-md"
                :float-label="getString('module.reference.detail.projectScope')"
                v-model="data.project_scope"
              />
              <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('contractor')"
                :error="fieldErrors.contractor && !data.contractor"
                type="text"
                class="full-width q-mb-md"
                :float-label="getString('module.reference.detail.contractor')"
                v-model="data.contractor"
              />
              <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('installer')"
                :error="fieldErrors.installer && !data.installer"
                type="text"
                class="full-width q-mb-md"
                :float-label="getString('module.reference.detail.installer')"
                v-model="data.installer"
              />
              <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('counselor')"
                :error="fieldErrors.counselor && !data.counselor"
                type="text"
                class="full-width q-mb-md"
                :float-label="getString('module.reference.detail.counselor')"
                v-model="data.counselor"
              />
              <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('consultant')"
                :error="fieldErrors.consultant && !data.consultant"
                type="text"
                class="full-width q-mb-md"
                :float-label="getString('module.reference.detail.consultant')"
                v-model="data.consultant"
              />
              <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('order_number')"
                :error="fieldErrors.order_number && !data.order_number"
                type="text"
                class="full-width q-mb-md"
                :float-label="getString(null, 'Order number', 'Bestelnummer')"
                v-model="data.order_number"
              />
              <q-input
                :disable="disable"
                :readonly="disable"
                v-if="fieldNames.includes('building_code')"
                :error="fieldErrors.building_code && !data.building_code"
                type="text"
                class="full-width q-mb-md"
                :float-label="getString(null, 'Building code', 'Bouwcode')"
                v-model="data.building_code"
              />
            </div>
          </div>
        </div>

        <!-- Metadata -->
        <div class="row q-mb-md">
          <div class="full-width">
            <p class="card-title">{{ getString('editor.block.header.meta') }}</p>
            <div class="card-block scroll">
              <div class="row">
                <!-- Keywords -->
                <q-input
                  v-if="fieldNames.includes('seo_keywords')"
                  :disable="disable"
                  :readonly="disable"
                  type="textarea"
                  class="no-shadow full-width q-mb-md"
                  :float-label="getString(null, 'SEO Keywords', 'SEO Trefwoorden')"
                  v-model="data.meta.seo.keywords"
                />
              </div>
              <div class="row">
                <div class="col-md-6 q-pr-md">
                  <q-input
                    :disable="disable"
                    :readonly="disable"
                    type="text"
                    class="no-shadow full-width q-mb-md"
                    :float-label="getString('editor.meta.seo.title')"
                    v-model="data.meta.seo.title"
                  />
                  <q-input
                    :disable="disable"
                    :readonly="disable"
                    type="text"
                    class="full-width q-mb-md"
                    :float-label="getString('editor.meta.og.image')"
                    clearable
                    v-model="data.meta.og.image.name"
                    @click="!disable && setModalContext('meta', true)"
                  />
                  <q-input
                    :disable="disable"
                    :readonly="disable"
                    type="text"
                    class="no-shadow full-width q-mb-md"
                    :float-label="getString('editor.meta.og.title')"
                    v-model="data.meta.og.title"
                  />
                  <q-checkbox
                    :disable="disable"
                    :readonly="disable"
                    class="full-width q-mb-md"
                    :label="getString('editor.meta.no_index')"
                    v-model="data.meta.no_index"
                  />
                </div>
                <div class="col-md-6 q-pl-md">
                  <q-input
                    :disable="disable"
                    :readonly="disable"
                    type="textarea"
                    class="full-width q-mb-md"
                    :float-label="getString('editor.meta.seo.description')"
                    v-model="data.meta.seo.description"
                  />
                  <q-input
                    :disable="disable"
                    :readonly="disable"
                    type="textarea"
                    class="full-width q-mb-md"
                    :float-label="getString('editor.meta.og.description')"
                    v-model="data.meta.og.description"
                  />
                  <q-input
                    :disable="disable"
                    :readonly="disable"
                    type="text"
                    class="full-width q-mb-md"
                    :float-label="getString('editor.meta.canonical')"
                    v-model="data.meta.canonical"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- image select modal -->
    <q-modal v-if="imageModal" v-model="imageModal">
      <file-select-modal :fileType="'image'" :guid="modalContext" />
    </q-modal>

    <!-- file select modal limited to pdf-->
    <q-modal v-if="fileModal" v-model="fileModal">
      <file-select-modal :guid="modalContext" :extension="'pdf'" />
    </q-modal>

    <!-- Modal to select templates -->
    <q-modal v-model="selectTemplateModal">
      <div class="q-ma-lg">
        <div class="row">
          <p>Template</p>
        </div>
        <div class="row q-mb-sm" v-for="temp in templates" :key="temp.id">
          <div class="col-8 q-pa-md">
            {{ temp.name }}
          </div>
          <div class="col-4 q-pa-md">
            <q-btn color="positive" @click="useTemplate(temp.id)">Select</q-btn>
          </div>
        </div>
      </div>
    </q-modal>

    <!-- Modal to save templates -->
    <q-modal v-model="newTemplateModal">
      <div class="q-pa-md">
        <div class="row">
          {{
            $store.state.stringsStore.language === 'nl'
              ? 'Opslaan als template'
              : 'Save as template'
          }}
        </div>
        <div class="q-ma-lg">
          <q-input v-model="newTemplateName" :float-label="getString('name')" />

          <q-select
            multiple
            v-model="restrictTemplate"
            :options="templateRestrictOptions"
            :float-label="'Modules'"
          />

          <div class="row q-mt-lg">
            <div class="col">
              <q-btn
                class="q-ml-md q-mr-md q-mt-lg"
                color="negative"
                @click="newTemplateModal = false"
              >
                {{ getString('action.cancel') }}
              </q-btn>
            </div>
            <div class="col">
              <q-btn class="q-ml-md q-mr-md q-mt-lg" color="positive" @click="saveTemplate">
                {{ getString('action.save') }}
              </q-btn>
            </div>
          </div>
        </div>
      </div>
    </q-modal>

    <!-- Block modal -->
    <q-modal v-model="blockModal">
      <q-modal-layout>
        <q-toolbar slot="header" color="tertiary">
          <q-toolbar-title>
            Add content block
          </q-toolbar-title>
          <q-btn flat round dense v-close-overlay icon="close" />
        </q-toolbar>
        <div class="q-pa-md">
          <div class="box">
            <div v-for="block in blocks" :key="block.id" v-bind:item="block">
              <div
                @click="selectBlock(block.code, newBlockIndex)"
                class="card-block card-block__dark ct-block q-pa-sm"
              >
                <div class="block-select">
                  <img
                    class="block-select__icon"
                    :src="require(`@/assets/icons/${getBlockIcon(block.code)}.svg`)"
                  />
                  <div class="block-select__name">
                    {{ getString(block.label) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </q-modal-layout>
    </q-modal>

    <q-modal v-model="compareModal">
      <vue-json-compare :oldData="oldData" :newData="newData"></vue-json-compare>
    </q-modal>
  </div>
</template>

<script>
/* eslint-disable import/no-dynamic-require */
/* eslint-disable global-require */
import moment from 'moment';
import qs from 'qs';
import draggable from 'vuedraggable';
import _isEmpty from 'lodash.isempty';

import createUuid from '@/helpers/createUuid';
import EventBus from '@/event-bus';
import getString from '@/helpers/GetStrings';
import flattenObject from '@/helpers/flattenObject';
import ContentBlock from '@/components/editor/content_blocks/ContentBlock.vue';

import ChipsAutocomplete from '@/components/ChipsAutocomplete.vue';
import FileSelectModal from '@/components/modal/FileSelectModal.vue';

import saveChanges from '@/mixins/detailViews/saveChanges';
import suggestUrl from '@/mixins/suggestUrl';
import _debounce from 'lodash.debounce';
import vueJsonCompare from 'vue-json-compare';

const TinyEditor = require('@tinymce/tinymce-vue').default;

export default {
  props: {
    previewModule: {
      type: String,
      default: '',
    },
    updatePossible: {
      type: Boolean,
      default: false,
    },
    canDelete: {
      type: Boolean,
      default: true,
    },
    publication: {
      type: Boolean,
      default: false,
    },
    reference: {
      type: Boolean,
      default: false,
    },
    hasAuthor: {
      type: Boolean,
      default: true,
    },
    permission: {
      type: String,
      required: true,
    },
    fields: {
      required: true,
      type: Array,
    },
    endpoint: {
      type: String,
      required: true,
    },
    module: {
      type: String,
      required: true,
    },
    contentOptional: {
      type: Boolean,
      default: false,
    },
    canLock: {
      type: Boolean,
      default: true,
    },
  },

  mixins: [saveChanges, suggestUrl, flattenObject],

  components: {
    draggable,
    ContentBlock,
    ChipsAutocomplete,
    FileSelectModal,
    TinyEditor,
    vueJsonCompare,
  },

  data() {
    return {
      oldData: null,
      newData: null,
      compareModal: false,
      displayHistory: false,
      newUpdate: false,
      testScroll: false,
      templates: [],
      scroll: false,
      templateRestrictOptions: [
        {
          label: getString('module.name.news'),
          value: 'news',
        },
        {
          label: getString('module.name.blog'),
          value: 'blog',
        },
        {
          label: getString('module.name.reference'),
          value: 'reference',
        },
        {
          label: getString('module.name.page'),
          value: 'page',
        },
        {
          label: getString('module.name.press'),
          value: 'press',
        },
        {
          label: getString('module.name.branch'),
          value: 'branches',
        },
      ],
      selectTemplateModal: false,
      newTemplateModal: false,
      restrictTemplate: [],
      newTemplateName: '',
      init: false,
      tempSave: null,
      dirty: false,
      fieldErrors: {},
      blockErrors: {},
      readOnly: false,
      urlSet: false,
      hasContent: false,
      targetOptions: [],
      targetGroupsOptions: [],
      resourceOptions: [],
      guid: createUuid(),
      dataLoaded: false,
      category: '',
      imageModal: false,
      fileModal: false,
      categoriesAvailible: [],
      tagName: '',
      tagsAvailible: [],
      drag: false,
      selection: null,
      contentBlocks: [],
      blocks: [],
      types: [],
      validations: {
        title: {
          set: false,
          error: false,
        },
        url: {
          set: false,
          error: false,
        },
        recap: {
          set: false,
          error: false,
        },
        categories: {
          set: false,
          error: false,
        },
      },
      firstPublished: null,
      data: {
        lightbox: '',
        read_confirmation: '',
        preview_hash: '',
        internal: false,
        auth_contact_people: false,
        parentId: null,
        parent: {
          name: '',
          id: null,
        },
        cover: {
          name: '',
          location: '',
        },
        implementation: '',
        client: '',
        branches: [],
        address: {
          city: '',
          street: '',
          streetnumber: '',
          zipcode: '',
        },
        map: false,
        delivery_date: '',
        architect: '',
        architectural_design: '',
        project_scope: '',
        contractor: '',
        installer: '',
        counselor: '',
        consultant: '',
        targets: [],
        auth_contact_people_groups: [],
        status: '',
        image: {
          ext: '',
          location: '',
          name: '',
        },
        author: {
          firstname: '',
          surname: '',
        },
        project: {
          id: null,
          number: null,
        },
        resource_author: {
          id: null,
          name: null,
        },
        recap: '',
        title: '',
        name: '',
        categories: [],
        tags: [],
        url: '',
        datetime_online: '',
        meta: {
          seo: {
            title: '',
            description: '',
            keywords: '',
          },
          og: {
            title: '',
            image: {
              name: '',
              location: '',
            },
            description: '',
          },
          slug: '',
          canonical: '',
        },
      },
      blockValues: [],
      promtNewTag: false,
      modalContext: null,
      minimized: true,
      disable: this.$route.params.id && true,
      blockModal: false,
      initialState: null,
      state: {
        initial: null,
        updated: null,
      },
      initialized: false,
      contentMissing: false,
    };
  },

  methods: {
    /**
     * Search for routes and modules using the autocomplete
     */
    searchProjects(terms, done) {
      this.axios.get(`project?autocomplete=${terms}`).then(response => {
        const suggestions = response.data.projects.map(item => ({
          label: item.number,
          value: item.id,
        }));
        done(suggestions);
      });
    },

    selectProject(found, keyboard) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;

      this.data.project = { id: found.value, number: found.label };
    },

    /**
     * Maps data from the block structure, for use in the content block
     * eg. { code: "foo", data: [{ name: "content", content: "bar"}] }
     * becomes: { foo: content: "bar"  }
     */
    mapBlockField(field) {
      const mappedField = {
        [field.code]: {},
      };

      field.data.forEach(element => {
        mappedField[field.code][element.name] = element[`${element.name}`];
      });

      return mappedField;
    },

    /**
     * Duplicate a content block.
     */
    duplicateBlock(block, index) {
      // First get the block into the correct format for use in the editor.
      const data = block.fields
        .map(b => this.mapBlockField(b))
        .reduce((a, v) => ({ ...a, [Object.keys(v)[0]]: Object.values(v)[0] }), {});

      // Add the formatted block to the
      this.addContentBlock(block.code, data, null, index);
    },

    getBlockIcon(code) {
      let icon;
      switch (code) {
        case 'default-header':
          icon = 'header-small';
          break;
        case 'default-text-plain':
          icon = 'text';
          break;
        case 'custom-youtube':
          icon = 'youtube';
          break;
        case 'custom-quote':
          icon = 'quote';
          break;
        case 'custom-text-layer':
          icon = 'text-over-image';
          break;
        case 'custom-text-image':
          icon = 'text-image';
          break;
        case 'custom-text-image-block':
          icon = 'text-image';
          break;
        case 'custom-text-image-news':
          icon = 'text-news';
          break;
        case 'custom-text-cta':
          icon = 'text-cta';
          break;
        case 'custom-assortment-category':
          icon = 'assortment-category';
          break;
        case 'custom-assortment-info':
          icon = 'assortment-info';
          break;
        case 'custom-downloads':
          icon = 'downloads';
          break;
        case 'custom-hub':
          icon = 'hub';
          break;
        case 'custom-usp':
          icon = 'usp';
          break;
        case 'custom-branches':
          icon = 'branches';
          break;
        case 'custom-header-small':
          icon = 'header-small';
          break;
        case 'custom-tiles':
          icon = 'tiles';
          break;
        case 'custom-widget':
          icon = 'widget';
          break;

        default:
          icon = 'text';
          break;
      }

      // Attempt to fetch the icon, set a fallback if it is not available.
      try {
        require(`@/assets/icons/${icon}.svg`);
      } catch (error) {
        console.warn(error);
        icon = 'text';
      }

      return icon;
    },

    openTemplateModal() {
      this.newTemplateModal = true;
    },

    handleToggle(type, value) {
      if (type === 'internal' && value) {
        this.data.auth_contact_people = !value;
      }
      if (type === 'auth_contact_people' && value) {
        this.data.internal = !value;
      }
    },

    selectBlock(code, index) {
      this.addContentBlock(code, {}, null, index);
      this.blockModal = false;
    },

    saveTemplate() {
      //
      this.axios
        .post(
          'block/templates',
          qs.stringify({
            name: this.newTemplateName,
            id: this.$route.params.id,
            module: this.endpoint,
            restrict: this.restrictTemplate,
          }),
        )
        .then(() => {
          this.$q.notify({
            message: 'Template saved successfully.',
            position: 'top-right',
            timeout: 1000,
            color: 'positive',
          });

          this.newTemplateModal = false;
          this.restrictTemplate = [];
          this.newTemplateName = '';
        })
        .catch(() => {
          this.$q.notify({
            message: 'Could not save the tamplate.',
            position: 'top-right',
            timeout: 1000,
            color: 'warning',
          });
        });
    },

    /**
     * Opens the block select modal.
     */
    openBlockModal(index) {
      this.newBlockIndex = index;
      this.blockModal = true;
    },

    fetchTemplates() {
      this.axios
        .get(`block/templates/?restricted=${this.endpoint}`)
        .then(res => {
          this.templates = res.data.templates;
          this.selectTemplateModal = true;
        })
        .catch(() => {});
    },

    useTemplate(id) {
      this.axios
        .get(`block/templates/${id}`)
        .then(res => {
          const temp = res.data.template;

          // Remove existing blocks first
          if (this.contentBlocks && this.contentBlocks.length) {
            this.contentBlocks.forEach(x => {
              this.removeContentBlock(x.guid);
            });
          }

          if (temp && temp.blocks) {
            for (let i = 0; i < temp.blocks.length; i += 1) {
              const block = temp.blocks[i];

              this.addContentBlock(block);
            }
          }

          this.selectTemplateModal = false;
        })
        .catch(() => {});
    },

    preview() {
      const site = process.env.VUE_APP_SITE;
      const url = `${site}${this.previewModule}/preview/${this.data.preview_hash}`;
      const win = window.open(url, '_blank');
      win.focus();
    },

    removeCover() {
      this.data.cover = {
        location: '',
        name: '',
      };
    },

    toggleHistory() {
      this.displayHistory = !this.displayHistory;
    },

    alertTitle(id) {
      this.axios.get(`blog/mutation/${id}`).then(async response => {
        const oldData = response.data.mutation.current ? response.data.mutation.current.data : {};
        const newData = response.data.mutation.new
          ? response.data.mutation.new.data
          : await this.axios.get(`${this.endpoint}/${this.$route.params.id}`).then(res => {
              const dataObject = Object.keys(res.data)[1];
              return res.data[dataObject];
            });

        delete newData.history;
        delete oldData.history;
        delete newData.locked;
        delete oldData.locked;

        this.oldData = oldData;
        this.newData = newData;
        this.compareModal = true;
      });
    },

    getTimeLineTitle(item) {
      return `${item.type === 'add' ? 'Created by ' : ''}${item.user.firstname} ${
        item.user.surname
      }`;
    },

    formatDateTime(val) {
      return moment(val).format('YYYY-MM-DD HH:mm:ss');
    },

    tinyConfig() {
      return {
        readOnly: false,
        height: 200,
        menubar: false,
        branding: false,
        browser_spellcheck: true,
        plugins: ['link'],
        // toolbar: [
        //   'undo redo | bold | pagebreak | link',
        // ],
        // style_formats: [
        //   { title: 'Paragraph', format: 'p' },
        //   { title: 'Heading 1', format: 'h1' },
        //   { title: 'Heading 2', format: 'h2' },
        // ],
        pagebreak_split_block: false,
      };
    },

    getString,

    async searchParentPage(terms, done) {
      // Request items based on terms given
      const results = await this.axios.get(`page?autocomplete=${terms.toLowerCase()}`);

      // Map only the item names/labels... to the suggested array
      const suggested = results.data.pages.map(item => ({ label: item.name, id: item.id }));

      // Suggested items found, return suggestions.
      if (suggested.length) {
        let filtered;
        // filter out the current page.
        if (this.$route.params.id) {
          filtered = suggested.filter(val => val.id !== Number(this.$route.params.id));
        } else {
          filtered = suggested;
        }

        done(filtered);
      } else {
        done([]);
      }
    },

    setParentPage(selection) {
      this.data.parent.name = selection.label;
      this.data.parent.id = selection.id;
    },

    blockError(guid) {
      return Object.keys(this.blockErrors).includes(guid);
    },

    expandCollapseAll() {
      this.minimized = !this.minimized;
      const guids = this.contentBlocks.map(block => block.guid);

      guids.forEach(guid => {
        EventBus.$emit(`block-expand-collapse:${guid}`, this.minimized);
      });
    },

    enableDisableFields() {
      if (!this.$route.params.id) {
        return;
      }
      const guids = this.contentBlocks.map(block => block.guid);

      guids.forEach(guid => {
        EventBus.$emit(`block-disable-enable:${guid}`, this.disable);
      });
    },

    getTargets() {
      this.axios
        .get('target')
        .then(res => {
          this.targetOptions =
            res.data &&
            res.data.targets &&
            res.data.targets.map(target => ({ label: target.label, value: target.id }));
        })
        .catch(err => {
          console.warn(err);
        });
    },

    getTargetGroupsOptions() {
      this.axios
        .get('contactpeopletargetgroup')
        .then(res => {
          this.targetGroupsOptions =
            res.data &&
            res.data.target_groups &&
            res.data.target_groups.map(target => ({ label: target.label, value: target.id }));
        })
        .catch(err => {
          console.warn(err);
        });
    },

    async searchResources(terms, done) {
      // Request items based on terms given
      const results = await this.axios.get(`resource?autocomplete=${terms.toLowerCase()}`);

      // Map only the item names/labels... to the suggested array
      const suggested = results.data.resources.map(resource => ({
        label: resource.fullname,
        value: resource.id,
      }));

      // Suggested items found, return suggestions.
      if (suggested.length) {
        let filtered;
        // filter out the current page.
        if (this.$route.params.id) {
          filtered = suggested.filter(val => val.id !== Number(this.$route.params.id));
        } else {
          filtered = suggested;
        }

        done(filtered);
      } else {
        done([]);
      }
    },

    setResourceAuthor(selection) {
      this.data.resource_author = {
        id: selection.value,
        name: selection.label,
      };
    },

    publicationChange() {
      switch (this.data.status) {
        case 'draft':
          this.data.datetime_online = null;
          break;
        case 'published':
          this.data.datetime_online = Date.now();
          break;
        case 'unpublished':
          this.data.datetime_online = this.firstPublished;
          break;
        default:
          break;
      }
    },

    // Takes full location as an argument and returns the nested upload folder location
    buildFileLocation(location) {
      if (!location) {
        return null;
      }
      return `${process.env.VUE_APP_API_URL}${location.replace('api/library', 'library/download')}`;
    },

    deletePost() {
      // Dialog
      this.$q
        .dialog({
          title: '',
          message: `*Delete content for - "${this.data.title || this.data.name}"?`,
          ok: getString('dialog.confirm.ok'),
          cancel: getString('dialog.confirm.cancel'),
        })
        .then(() => {
          // delete call
          this.axios
            .delete(`${this.endpoint}/${this.$route.params.id}`)
            .then(() => {
              // Refresh data to see the result
              this.$q.notify({
                message: this.getString('upload.delete.success').replace(
                  '%file%',
                  this.data.title || this.data.name,
                ),
                position: 'top-right',
                timeout: 1000,
                color: 'positive',
              });
              this.$router.push(`/${this.module}`);
            })
            .catch(error => {
              // Error
              console.warn(error);
            });
        })
        .catch(() => {
          console.warn('Post not removed');
        });
    },

    isValid(requiredFields) {
      return Object.values(requiredFields).every(
        value => value !== '' && value !== undefined && value !== null,
      );
    },

    getFields() {
      // required fields
      const requiredFields = {};

      // clean url
      if (this.data.url) {
        this.cleanUrl(this.data.url);
      }

      // Apply required fields given to the component as props
      this.fields.forEach(field => {
        if (field.required === true) {
          // retrieve location from image field
          if (field.name === 'image') {
            Object.assign(requiredFields, {
              image:
                this.data.image &&
                this.data.image.location &&
                this.data.image.location.replace('api/', ''),
            });
          } else if (field.name === 'city') {
            Object.assign(requiredFields, { city: this.data.address.city });
          } else if (field.name === 'cover') {
            Object.assign(requiredFields, {
              cover:
                this.data.cover &&
                this.data.cover.location &&
                this.data.cover.location.replace('api/', ''),
            });
            // Assign all other fields.
          } else {
            Object.assign(requiredFields, { [field.name]: this.data[field.name] });
          }
        }
      });

      const optional = {
        notes: this.data.notes,
        recap: this.data.recap,
        street: this.data.address && this.data.address.street,
        streetnumber: this.data.address && this.data.address.streetnumber,
        zipcode: this.data.address && this.data.address.zipcode,
        parent: this.data.parent && this.data.parent.id,
        meta_seo_title: this.data.meta.seo.title,
        meta_seo_description: this.data.meta.seo.description,
        meta_seo_keywords: this.data.meta.seo.keywords,
        meta_og_title: this.data.meta.og.title,
        meta_og_description: this.data.meta.og.description,
        meta_og_image:
          this.data.meta.og.image &&
          this.data.meta.og.image.name &&
          this.data.meta.og.image.location &&
          this.data.meta.og.image.location.replace('api/', ''),
        meta_slug: this.data.meta.slug,
        meta_canonical: this.data.meta.canonical,
        meta_no_index: this.data.meta.no_index ? 1 : 0,
      };

      if (this.newUpdate === true) {
        optional.update = 1;
      }

      if (this.data.content !== false) {
        // For the publication structure, it is only possible to save the PS without content when there was no content to start with.
        // When a PS already contains content, it is a required field when saving
        if (this.contentOptional) {
          if (this.hasContent || this.contentBlocks.length) {
            Object.assign(requiredFields, {
              content: this.contentBlocks.length
                ? JSON.stringify(this.formatBlock(this.contentBlocks))
                : null,
            });
          }
        } else {
          Object.assign(requiredFields, {
            content: this.contentBlocks.length
              ? JSON.stringify(this.formatBlock(this.contentBlocks))
              : null,
          });
        }
      }

      // If status is 'draft', its optional
      if (requiredFields.status === 'draft' && this.data.datetime_online) {
        Object.assign(optional, {
          datetime_online: moment(this.data.datetime_online)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss'),
        });
      } else if (requiredFields.status === 'published') {
        Object.assign(requiredFields, {
          datetime_online: moment(this.data.datetime_online)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss'),
        });
      } else if (requiredFields.status === 'unpublished') {
        Object.assign(requiredFields, {
          datetime_online: moment(this.firstPublished)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss'),
        });
      }

      // If the page is an index, remove url from the fields list.
      if (this.data.index === true) {
        delete requiredFields.url;
      }

      const errors = {};

      // Make sure all required fields are valid
      Object.keys(requiredFields).forEach(field => {
        // Ignore content fields, these are handled seperately
        if (field !== 'content') {
          // Targets are only required when posts are not internal
          if (field === 'targets') {
            // Target field is required only if post is not internal.
            if (!requiredFields.internal && requiredFields.targets.length === 0) {
              errors.targets = true;
            }
          } else if (field === 'image') {
            if (this.data.image && !this.data.image.location) {
              errors.image = true;
            }
          } else if (field === 'city') {
            if (!this.data.address.city) {
              errors.city = true;
            }
          } else if (Array.isArray(this.data[field]) && this.data[field].length === 0) {
            errors[field] = true;
          } else if (
            field === 'online' ||
            field === 'internal' ||
            field === 'map' ||
            field === 'auth_contact_people'
          ) {
            // Ignore, will send false on undefined, null, false
          } else if (!this.data[field]) {
            errors[field] = true;
          }
        } else if (!this.contentOptional && !this.data.content) {
          this.contentMissing = true;
        }
      });

      this.fieldErrors = errors;

      // Join required and optional
      const fields = Object.assign(requiredFields, optional);
      return fields;
    },

    requestParams(errorChecking = true) {
      let fields = {};
      if (errorChecking) {
        fields = this.getFields();
      }

      const clearable = [
        'meta_seo_title',
        'meta_seo_description',
        'meta_seo_keywords',
        'meta_og_title',
        'meta_og_description',
        'meta_og_image',
        'meta_slug',
        'meta_canonical',
        'architect',
        'architectural_design',
        'contractor',
        'installer',
        'counselor',
        'consultant',
        'consultant',
      ];

      // If a value is null, undefined or empty, remove it from fields
      for (const [key, value] of Object.entries(fields)) {
        // Add exception for empt
        if (clearable.includes(key)) {
          break;
        }

        if (!value) delete fields[key];
      }

      // Add each tag
      if (this.data.tags) {
        Object.assign(fields, { tags: this.data.tags.map(tag => tag.id) });
      }

      if (this.data.internal !== undefined) {
        Object.assign(fields, { internal: this.data.internal ? 1 : 0 });
      }

      if (this.data.auth_contact_people !== undefined) {
        Object.assign(fields, { auth_contact_people: this.data.auth_contact_people ? 1 : 0 });
      }

      if (this.data.map !== undefined) {
        Object.assign(fields, { map: this.data.map ? 1 : 0 });
      }

      if (this.data.online !== undefined) {
        Object.assign(fields, { online: this.data.online ? 1 : 0 });
      }

      // Add each category
      if (this.data.categories) {
        Object.assign(fields, { categories: this.data.categories.map(category => category.id) });
      }

      // Add each branch
      if (this.data.branches) {
        delete fields.branches;

        Object.assign(fields, { branches: this.data.branches.map(branch => branch.id) });
      }

      if (this.data.delivery_date) {
        Object.assign(fields, {
          delivery_date: moment(this.data.delivery_date).format('YYYY-MM-DD'),
        });
      }

      if (this.data.project_scope) {
        Object.assign(fields, { project_scope: this.data.project_scope });
      }

      // Add each category
      if (this.data.targets) {
        Object.assign(fields, { target: this.data.targets });
      }

      // Add target groups from logged contact people
      if (this.data.auth_contact_people_groups) {
        Object.assign(fields, { auth_contact_people_groups: this.data.auth_contact_people_groups });
      }

      // Add project id
      if (this.data.project) {
        Object.assign(fields, { project: this.data.project.id });
      }

      // Add resource_author id
      if (this.data.resource_author) {
        Object.assign(fields, { resource_author: this.data.resource_author.id });
      }

      fields.lightbox = this.data.lightbox ? Number(this.data.lightbox) : 0;

      fields.read_confirmation = this.data.read_confirmation
        ? Number(this.data.read_confirmation)
        : 0;

      return fields;
    },

    fullname(first, last) {
      return `${first} ${last}`;
    },

    async checkAndSave(newUpdate = false) {
      if (!this.contentOptional && !this.contentBlocks.length) {
        this.$q.notify({
          message: getString(
            null,
            'Select at least one content block',
            'Selecteer minimaal één content blok',
          ),
          type: 'negative',
          timeout: 3000,
          position: 'top-right',
        });
        return;
      }
      this.newUpdate = false;
      if (
        this.disable &&
        this.data.locked &&
        this.data.locked.user.id !== this.$store.state.meStore.me.id
      ) {
        // Only super users may forcefully take over editing
        if (
          this.$store.state.meStore.me.su ||
          this.$store.state.meStore.me.rights.includes(`${this.endpoint}.unlock`)
        ) {
          this.$q
            .dialog({
              title: '',
              message: getString('content.lock.dialog.claim').replace(
                '%name%',
                this.fullname(
                  this.data.locked.user.name.firstname,
                  this.data.locked.user.name.surname,
                ),
              ),
              ok: getString('dialog.confirm.ok'),
              cancel: getString('dialog.confirm.cancel'),
            })
            .then(() => {
              // Request lock
              this.axios
                .put(`${this.endpoint}/${this.$route.params.id}/lock`)
                .then(() => {
                  // Refresh data to see the result
                  this.$q.notify({
                    message: 'You may now edit the post.',
                    position: 'top-right',
                    timeout: 1000,
                    color: 'positive',
                  });
                  this.disable = false;
                  this.data.locked = false;
                  this.enableDisableFields();
                })
                .catch(() => {
                  // Refresh data to see the result
                  this.$q.notify({
                    message:
                      'Request to edit the post has failed, you may not have the correct permission.',
                    position: 'top-right',
                    timeout: 2000,
                    color: 'warning',
                  });
                });
            })
            .catch(() => {});
        } else {
          this.$q.notify({
            message: `The post is currently being edited by ${this.data.locked.user.name.firstname} ${this.data.locked.user.name.surname}.`,
            type: 'warning',
            timeout: 2000,
            position: 'top-right',
          });
        }

        return;
      }
      if (this.disable) {
        // Enable editing
        if (this.$route.params.id) {
          if (this.canLock) {
            await this.axios.put(`${this.endpoint}/${this.$route.params.id}/lock`);
          }
          this.disable = false;
          this.enableDisableFields();
        }
        return;
      }

      this.getFields();

      if (_isEmpty(this.blockErrors) && _isEmpty(this.fieldErrors)) {
        this.newUpdate = newUpdate;
        this.saveChanges(false);

        if (this.$route.params.id) {
          this.disable = true;
          // Update disabled state on the content blocks
          this.enableDisableFields();
        }
      } else {
        console.warn('Field errors:', Object.keys(this.fieldErrors));
        console.warn('Block errors:', Object.keys(this.blockErrors));
        if (this.blockErrors) {
          for (const property in this.blockErrors) {
            if (property) {
              this.$q.notify({
                message:
                  this.$store.state.stringsStore.language === 'nl'
                    ? 'Controleer alle velden van blokken en probeer opnieuw'
                    : 'Check for missing fields in the blocks and try again',
                type: 'negative',
                timeout: 3000,
                position: 'top-right',
              });
            }
          }
        }
        if (this.fieldErrors) {
          this.$q.notify({
            message:
              this.$store.state.stringsStore.language === 'nl'
                ? 'Controleer alle velden en probeer opnieuw'
                : 'Check for missing fields and try again',
            type: 'negative',
            timeout: 3000,
            position: 'top-right',
          });
        }
      }
      // Set warnings for missing fields within this component
      this.dirty = true;

      // Check the inputs in this component
      // For each block, publish information about the errors
      this.contentBlocks.forEach(block => {
        EventBus.$emit(`block-errors:${block.guid}`, this.blockErrors[block.guid] || {});
      });

      this.$emit('update', false);
    },

    formatBlock(contentBlock) {
      const errors = {};
      // Returns the formatted block as a json string to be saved.
      const formatted = contentBlock.map(block => ({
        // Code of the block, eg. "default-header"
        code: block.code,

        // Id for the block
        id: block.id,

        // Data is an array consisting of all the fields for the block as objects.
        data:
          block.data &&
          block.data.length &&
          block.data
            .map((field, fieldIndex) => ({
              // Map each data field code as the object key
              // data: [{ code: 'default-header-image' } ....] => data: { default-header-image: { content: '...} }
              [field.code]:
                field.data.length &&
                field.data
                  .map((dataContent, index) => {
                    if (
                      (field.required === true || field.required === undefined) &&
                      (!dataContent[dataContent.name] || dataContent[dataContent.name].length === 0)
                    ) {
                      // If a field is required, en error is set on the guid of the block with the index of the field and the input
                      errors[block.guid] = errors[block.guid] || {};
                      errors[block.guid][fieldIndex] = errors[block.guid][fieldIndex] || {};
                      errors[block.guid][fieldIndex][index] = true;
                    }

                    // Return a null object if a field is empty and not required
                    if (field.required === false && dataContent[dataContent.name] === '') {
                      return { [dataContent.name]: null };
                    }

                    // Return the formated field
                    return {
                      [dataContent.name]: dataContent[dataContent.name],
                    };
                  })
                  .reduce((result, item) => {
                    const key = Object.keys(item)[0];
                    result[key] = item[key];
                    return result;
                  }, {}),
            }))
            .reduce((result, item) => {
              const key = Object.keys(item)[0];
              result[key] = item[key];
              return result;
            }, {}),
      }));

      // Set blockErrors
      this.blockErrors = errors;

      return formatted;
    },

    arraymove(arr, fromIndex, toIndex) {
      const element = arr[fromIndex];
      arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, element);
    },

    addContentBlock(code, data = {}, block = null, index = null) {
      if (!code) {
        return;
      }

      // Assign a guid to the block. (needed to listen to changes on the content).
      const guid = createUuid();

      // Assign the guid to the saved content
      if (block !== null) {
        block.guid = guid;
      }

      if (index !== null) {
        this.data.content.splice(index + 1, 0, data);
        this.contentBlocks.splice(index + 1, 0, { code, guid, data });
      } else {
        if (this.data.content) {
          this.data.content.push(data);
        }
        this.contentBlocks.push({
          id: block && block.id ? block.id : guid.split('-').pop(),
          code,
          guid,
          data,
        });
      }

      // Register an event listener for the block
      EventBus.$on(`update-block:${guid}`, payload => {
        // Update the content for the block.
        this.updateContentBlock(payload, guid);

        // Update the error status for the block header
        this.blockError(guid);

        // Check all values for null, empty, undefined
        Object.values(this.flattenObject(this.formatBlock(this.contentBlocks)));

        this.updateState();
      });
    },

    updateContentBlock(payload, guid) {
      // Called when an event is fired by a content block.
      // Select the block by guid.
      const block = this.contentBlocks.find(val => val.guid === guid);

      // If the block exists, update the block content.
      if (block) {
        block.data = payload.fields;
        block.id = payload.id;
      }
    },

    removeContentBlock(guid) {
      this.contentBlocks = this.contentBlocks.filter(val => val.guid !== guid);
      // Remove from saved content as well
      if (this.data.content) {
        this.data.content = this.data.content.filter(val => val.guid !== guid);
      }
      // Stop listening to events on the block.
      EventBus.$off(`update-block:${guid}`);
    },

    async getContentBlocks() {
      if (!this.$store.state.editorStore.fields) {
        await this.$store.dispatch('getFieldTypes');
      }

      if (!this.$store.state.editorStore.blocks) {
        await this.$store.dispatch('getBlocks');
      }

      this.$store.state.editorStore.fields.forEach(async type => {
        if (type !== 'base' && !this.$store.state.editorStore.types[type]) {
          await this.$store.dispatch('getType', type);
        }
      });

      this.types = this.$store.state.editorStore.types;
      this.blocks = this.$store.state.editorStore.blocks;
    },

    setModalContext(context = null, show = false) {
      this.modalContext = context === 'meta' ? 'meta' : this.guid;
      this.imageModal = !!show;
    },

    dateRangeYear(min, plus) {
      const d = new Date();
      const current = d.getFullYear();
      const range = [];
      for (let index = -min; index < plus; index += 1) {
        range.push({ label: `${current + index}`, value: current + index });
      }
      return range;
    },

    getDetail() {
      this.axios.get(`${this.endpoint}/${this.$route.params.id}`).then(res => {
        const dataObject = Object.keys(res.data)[1];
        this.data = res.data[dataObject];
        this.formatData(res.data[dataObject]);
      });
    },

    formatData(data) {
      if (this.data.url) {
        this.urlSet = true;
      }

      if (!data.cover) {
        this.$set(this.data, 'cover', {});
        this.$set(this.data.cover, 'name', '');
        this.$set(this.data.cover, 'location', '');
      }

      if (this.endpoint === 'page' && !data.parent) {
        this.$set(this.data, 'parent', {});
        this.$set(this.data.parent, 'id', '');
        this.$set(this.data.parent, 'name', '');
      }

      if (this.endpoint === 'reference' && !data.project) {
        this.$set(this.data, 'project', {});
        this.$set(this.data.project, 'id', null);
        this.$set(this.data.project, 'number', null);
      }

      if (this.endpoint === 'blog' && !data.resource_author) {
        this.$set(this.data, 'resource_author', {});
        this.$set(this.data.resource_author, 'id', null);
        this.$set(this.data.resource_author, 'name', null);
      }

      this.data.meta.og.image = this.data.meta.og.image || { name: '', location: '' };

      // Store the original publication date
      this.firstPublished = this.data.status === 'published' && this.data.datetime_online;

      if (this.data.content && this.data.content.length) {
        // Add a variable to prevent removing content from publication structure
        this.hasContent = true;
        this.data.content.forEach(block => {
          this.addContentBlock(block.code, block.data, block, null);
        });
      }

      // Apply selected tags from props
      if (this.data.tags && this.data.tags.length) {
        this.tagsSelected = this.data.tags;
      }

      // Apply selected tags from props
      if (this.data.targets && this.data.targets.length) {
        this.data.targets = this.data.targets.map(target => target.id);
      }

      if (this.data.auth_contact_people_groups && this.data.auth_contact_people_groups.length) {
        this.data.auth_contact_people_groups = this.data.auth_contact_people_groups.map(
          group => group.id,
        );
      }

      this.dataLoaded = true;
    },

    updateState() {
      if (this.disable || !this.dataLoaded) {
        return;
      }
      this.$emit('update', { module: this.module, data: this.data, blocks: this.contentBlocks });
    },

    titleBottom() {
      const title = document.querySelector('#titlebar');
      if (!title) {
        return 0;
      }
      const titleHeight = title.scrollHeight;
      return `${titleHeight}px`;
    },
  },

  computed: {
    linkedIds() {
      const blocks = JSON.parse(JSON.stringify(this.contentBlocks));
      return blocks.map(block => ({ value: `#${block.id}`, title: block.id }));
    },
    imgSrc() {
      return this.data.image && this.data.image.location;
    },
    fieldNames() {
      return this.fields.map(field => field.name);
    },

    author() {
      if (this.data.author) {
        if (this.data.author.firstname || this.data.author.surname) {
          return [
            this.data.author.firstname ? this.data.author.firstname : null,
            this.data.author.surname ? this.data.author.surname : null,
          ]
            .filter(val => val !== null)
            .join(' ');
        }
      }
      return [
        this.$store.state.meStore.me.name ? this.$store.state.meStore.me.name.firstname : null,
        this.$store.state.meStore.me.name ? this.$store.state.meStore.me.name.surname : null,
      ]
        .filter(val => val !== null)
        .join(' ');
    },

    publicationOptions() {
      const options = [
        {
          label: getString('publication.option.draft'),
          value: 'draft',
        },
        {
          label: getString('publication.option.select'),
          value: 'published',
        },
      ];
      if (this.firstPublished) {
        options.push({
          label: getString('publication.option.unpublish'),
          value: 'unpublished',
        });
      }
      return options;
    },

    blockOptions() {
      // Format the select options for q-select.
      return this.blocks.map(opt => ({ value: opt.code, label: getString(opt.label) }));
    },

    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
      };
    },
  },

  watch: {
    drag() {
      EventBus.$emit('content-editor:dragging', this.drag);
    },
    data: {
      deep: true,

      handler: _debounce(function() {
        if (this.dataLoaded) {
          // this.$emit('update', { module: this.module, data: this.data, blocks: JSON.stringify(this.contentBlocks) });
          this.updateState();
        }
      }, 300),
    },
  },

  async mounted() {
    const page = document.querySelector('.q-layout-page');
    page.addEventListener('scroll', e => {
      this.testScroll = e.target.scrollTop > 50;
    });

    this.$store.dispatch('getBlocks');

    // Apply modal context
    this.setModalContext();

    // Register event for removing content blocks.
    EventBus.$on('removeblock', guid => {
      this.removeContentBlock(guid);
    });

    EventBus.$on('chips-autocomplete:tags', selection => {
      this.data.tags = selection;
    });

    EventBus.$on('chips-autocomplete:branches', selection => {
      this.data.branches = selection;
    });

    EventBus.$on('chips-autocomplete:categories', selection => {
      this.data.categories = selection;
    });

    EventBus.$on('file-select-close', () => {
      this.imageModal = false;
      this.fileModal = false;
    });

    EventBus.$on('file-select-close', () => {
      this.imageModal = false;
      this.fileModal = false;
    });

    EventBus.$on('file-select:meta', data => {
      this.setModalContext();

      this.data.meta.og.image = { location: data.location, name: data.name };
      this.imageModal = false;
      this.fileModal = false;
    });

    EventBus.$on(`file-select:${this.guid}`, data => {
      if (data.type === 'image') {
        this.imageSelected = true;
        this.imgSrc = data.location;
        if (this.data.image) {
          this.data.image.location = data.location;
        } else {
          // Added in case of an incoming post where image in null
          this.data.image = { location: data.location };
        }
      }

      this.data.cover = data;

      this.imageModal = false;
      this.fileModal = false;
    });

    const savedState = this.$store.getters.getEditorState(this.module, this.$route.params.id);
    const askForResume =
      savedState &&
      (savedState.blocks.length > 0 ||
        savedState.content.length > 0 ||
        Object.entries(savedState.data).length > 0);

    if (askForResume) {
      this.$q
        .dialog({
          title: '',
          message: this.getString('editor.savedstate.resume'),
          ok: this.getString('dialog.confirm.ok'),
          cancel: this.getString('dialog.confirm.cancel'),
        })
        .then(() => {
          this.data = savedState.data;
          let content;
          if (savedState.content) {
            content = JSON.parse(savedState.content);
          } else {
            content = savedState.data.content;
          }

          // Remove content stored in the data object
          // if (this.data.content && this.data.content.length) {
          //   this.data.content.forEach((block) => {
          //     this.removeContentBlock(block.guid);
          //   });
          // }

          if (content && content.length) {
            content.forEach(block => {
              this.addContentBlock(block.code, block.data, block, null);
            });
            const parsedContent = this.formatBlock(content);

            this.data.content = parsedContent;
          }

          this.dataLoaded = true;
        })
        .catch(() => {
          if (this.$route.params.id) {
            this.getDetail();
          } else {
            this.dataLoaded = true;
          }
        });
    } else if (this.$route.params.id) {
      await this.getDetail();
    } else {
      if (this.fieldNames.includes('online')) {
        this.$set(this.data, 'online', true);
      }
      this.dataLoaded = true;
      this.getTargets();
      this.getTargetGroupsOptions();
    }
    // Fetch content blocks on load.
    await this.getContentBlocks();

    // Get targets
    await this.getTargets();
    await this.getTargetGroupsOptions();
    // Check if there is a saved state for the current module
  },

  destroyed() {
    clearInterval(this.tempSave);
    // Clean up
    EventBus.$off('file-select-close');
    EventBus.$off('file-select-close');
    EventBus.$off(`file-select:${this.guid}`);
    EventBus.$off('file-select:meta');
    EventBus.$off('removeblock');
    EventBus.$off('chips-autocomplete:tags');
    EventBus.$off('chips-autocomplete:categories');
    EventBus.$off('chips-autocomplete:branches');

    // const page = document.querySelector('.q-layout-page');
    // if (page) {
    //   page.removeEventListener('scroll', null);
    // }
  },
};
</script>

<style lang="stylus" scoped>
.center {
  display: flex;
  justify-content: center;
}

.q-layout-page {
  // overflow: hidden !important;
}

.scrolly {
  height: calc(100vh - 150px) !important;
  overflow: auto;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  > div:last-child {
    margin-bottom: 30px !important;
  }
}

.element {
  width: fit-content;
  display: flex;
  border: 2px #ACACAC dashed;
}

.image-preview.image {
  padding: 0;
  height: 200px;
  background-image: linear-gradient(45deg, #F3F3F3 25%, transparent 5%), linear-gradient(-45deg, #F3F3F3 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #F3F3F3 75%), linear-gradient(-45deg, transparent 75%, #F3F3F3 75%);
}

.centered {
  text-align: center;
  display: block;
  margin-left: auto;
  margin-right: auto;
}

.image-preview {
  height: 40vh;
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  position: relative;

  img {
    max-width: 100%;
    max-height: 100%;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
}

.flex-center {
  display: flex;
  justify-content: center;
}

.flex-between {
  display: flex;
  justify-content: space-between;
}

.content-section {
  vertical-align: middle;
  justify-content: space-between;

  &__header {
    margin: 20px 0;
  }
}

.sticky-edit {
  position: fixed;
  top: 80px;
  background: #55555517;
  border-radius: 2px;
  border: 1px #ACACAC17 solid;
  padding: 15px;
  right: 50px;

  // z-index 999
  button {
    box-shadow: 0 4px 5px rgba(0, 0, 0, 0.05);
  }
}

.sticky {
  padding: 20px 0;
  background: #e7e7e7;
  // z-index: 10;
}

.sticky-scroll {
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.1);
}

.box {
  width: 900px;
  display: flex;
  flex-wrap: wrap;
}

.box>* {
  flex: 0 0 30%;
  height: 70px;
  margin: 10px auto;
}

.ct-block {
  font-size: 12px;
}

.add-block {
  display: flex;
  justify-content: center;
  margin: 12.5px 0;
  position: relative;
  z-index: 0;
}

.block-seperator {
  position: absolute;
  top: 12.5px;
  height: 1px;
  width: 100%;
  background-color: #acacac;
}

.block-select {
  display: flex;
  height: 100%;
  flex-direction: row;
  cursor: pointer;

  &__icon {
    flex: 1;
    margin-right: 10px;
  }

  &__name {
    flex: 3;
    margin: auto 0;
  }
}

.wrap-title {
  max-width: calc(100% - 200px);
}

.sticky-editor {
  position: sticky;

  &__top {
    top: 33px !important;
    z-index: 10;
  }

  &__content {
    top: 120px;
  }
}

.content-error {
  border: 1px solid red;
}

.sticky-shadow {
  transition: all 0.5s;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
}
</style>
